export const TRIGGER_CONTROL_TYPES = ['select', 'select_dropdown'] as const
export const DEPENDENT_CONTROL_TYPES = ['select', 'select_dropdown'] as const

export type CheckpointDependency = {
  /** `sectionName.subSectionName.checkpointName` */
  reference: string | null
  mappings: Record<string, string[]>
}

export type FlatCheckpoint = {
  name: string
  title: string
  control_type: string
  data_source: any[]
  sectionIndex: number
  subIndex: number
  checkpointIndex: number
  sectionName: string
  subSectionName: string
  reference: string
  checkpoint: any
}

const byIndex = (a: any, b: any) => (a?.index ?? 0) - (b?.index ?? 0)

export function equalsIgnoreCase(a: any, b: any): boolean {
  return String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase()
}

export function includesIgnoreCase(list: any[] = [], value: any): boolean {
  const lower = String(value ?? '').toLowerCase()
  return (list || []).some((v) => String(v).toLowerCase() === lower)
}

/** Find mapping object key matching trigger value (case-insensitive). */
export function findMappingKey(
  mappings: Record<string, string[]> | null | undefined,
  triggerValue: string | null | undefined
): string | null {
  if (!mappings || triggerValue == null || triggerValue === '') return null
  if (Object.prototype.hasOwnProperty.call(mappings, triggerValue)) {
    return triggerValue
  }
  const lower = String(triggerValue).toLowerCase()
  return (
    Object.keys(mappings).find((key) => key.toLowerCase() === lower) || null
  )
}

export function getMappedValues(
  mappings: Record<string, string[]> | null | undefined,
  triggerValue: string | null | undefined
): string[] {
  const key = findMappingKey(mappings, triggerValue)
  return key ? mappings?.[key] || [] : []
}

export function buildCheckpointReference(
  sectionName: string,
  subSectionName: string,
  checkpointName: string
): string {
  return `${sectionName}.${subSectionName}.${checkpointName}`
}

export function parseCheckpointReference(
  reference: string | null | undefined
): {
  sectionName: string
  subSectionName: string
  checkpointName: string
} | null {
  if (!reference) return null
  const parts = String(reference).split('.')
  if (parts.length < 3) return null
  return {
    sectionName: parts[0],
    subSectionName: parts[1],
    checkpointName: parts.slice(2).join('.'),
  }
}

export function hasDependency(checkpoint: any): boolean {
  return Boolean(checkpoint?.options?.dependency)
}

export function getDependency(checkpoint: any): CheckpointDependency | null {
  const dep = checkpoint?.options?.dependency
  if (!dep) return null
  // Prefer `reference`; accept legacy `trigger_name` as checkpoint-only path
  if (dep.reference != null || dep.trigger_name == null) {
    return {
      reference: dep.reference ?? null,
      mappings: dep.mappings || {},
    }
  }
  return {
    reference: dep.trigger_name,
    mappings: dep.mappings || {},
  }
}

export function hasConfiguredDependency(checkpoint: any): boolean {
  const dep = getDependency(checkpoint)
  if (!dep) return false
  return Boolean(dep.reference) || Object.keys(dep.mappings || {}).length > 0
}

export function createEmptyDependency(): CheckpointDependency {
  return { reference: null, mappings: {} }
}

export function clearDependency(checkpoint: any) {
  if (!checkpoint?.options?.dependency) return
  delete checkpoint.options.dependency
}

export function buildInitialMappings(
  triggerDataSource: any[] = [],
  dependentDataSource: any[] = []
): Record<string, string[]> {
  const allValues = (dependentDataSource || []).map((d) => d.value)
  const mappings: Record<string, string[]> = {}
  for (const opt of triggerDataSource || []) {
    mappings[opt.value] = [...allValues]
  }
  return mappings
}

export function syncMappingsOnOptionRemove(
  mappings: Record<string, string[]> = {},
  removedValue: string
): Record<string, string[]> {
  const next: Record<string, string[]> = {}
  for (const [triggerValue, values] of Object.entries(mappings)) {
    next[triggerValue] = (values || []).filter(
      (v) => !equalsIgnoreCase(v, removedValue)
    )
  }
  return next
}

export function syncMappingsOnTriggerOptionAdd(
  mappings: Record<string, string[]> = {},
  triggerValue: string,
  dependentDataSource: any[] = []
): Record<string, string[]> {
  const existingKey = findMappingKey(mappings, triggerValue)
  if (existingKey) return mappings
  return {
    ...mappings,
    [triggerValue]: (dependentDataSource || []).map((d) => d.value),
  }
}

export function syncMappingsOnTriggerOptionRemove(
  mappings: Record<string, string[]> = {},
  removedTriggerValue: string
): Record<string, string[]> {
  const key = findMappingKey(mappings, removedTriggerValue)
  if (!key) return mappings
  const next = { ...mappings }
  delete next[key]
  return next
}

export function orderSectionsByIndex(sections: any[] = []): any[] {
  return [...(sections || [])].sort(byIndex).map((section) => ({
    ...section,
    sub_sections: [...(section.sub_sections || [])]
      .sort(byIndex)
      .map((sub) => ({
        ...sub,
        checkpoints: [...(sub.checkpoints || [])].sort(byIndex),
      })),
  }))
}

export function flattenCheckpoints(sections: any[] = []): FlatCheckpoint[] {
  const result: FlatCheckpoint[] = []
  orderSectionsByIndex(sections).forEach((section, sectionIndex) => {
    const sectionName = section?.name || ''
    ;(section.sub_sections || []).forEach((sub, subIndex) => {
      const subSectionName = sub?.name || ''
      ;(sub.checkpoints || []).forEach((cp, checkpointIndex) => {
        if (!cp?.name || !sectionName || !subSectionName) return
        result.push({
          name: cp.name,
          title: cp.title || cp.name,
          control_type: cp.control_type,
          data_source: cp.data_source || [],
          sectionIndex,
          subIndex,
          checkpointIndex,
          sectionName,
          subSectionName,
          reference: buildCheckpointReference(
            sectionName,
            subSectionName,
            cp.name
          ),
          checkpoint: cp,
        })
      })
    })
  })
  return result
}

export function findCheckpointByReference(
  sections: any[],
  reference: string | null | undefined
): FlatCheckpoint | null {
  if (!reference) return null
  return (
    flattenCheckpoints(sections).find((cp) => cp.reference === reference) ||
    null
  )
}

export function getTriggerCandidates(
  sections: any[],
  sectionIndex: number,
  subIndex: number,
  checkpointIndex: number
): FlatCheckpoint[] {
  const current = flattenCheckpoints(sections).find(
    (cp) =>
      cp.sectionIndex === sectionIndex &&
      cp.subIndex === subIndex &&
      cp.checkpointIndex === checkpointIndex
  )
  if (!current) return []

  return flattenCheckpoints(sections).filter((candidate) => {
    if (candidate.checkpoint === current.checkpoint) return false
    if (candidate.reference === current.reference) return false
    if (!TRIGGER_CONTROL_TYPES.includes(candidate.control_type as any)) {
      return false
    }
    if (hasDependency(candidate.checkpoint)) return false
    return true
  })
}

export function mappingHasEmptyTriggerValue(
  dependency: CheckpointDependency | null | undefined,
  triggerDataSource: any[] = []
): boolean {
  if (!dependency?.reference) return true
  const triggerValues = (triggerDataSource || []).map((d) => d.value)
  if (!triggerValues.length) return true
  return triggerValues.some(
    (triggerValue) => !getMappedValues(dependency.mappings, triggerValue).length
  )
}

export function templateHasInvalidDependency(sections: any[]): boolean {
  for (const item of flattenCheckpoints(sections)) {
    const dependency = getDependency(item.checkpoint)
    if (!dependency) continue
    if (
      !(DEPENDENT_CONTROL_TYPES as readonly string[]).includes(
        item.control_type
      )
    ) {
      return true
    }
    const trigger = findCheckpointByReference(sections, dependency.reference)
    if (!trigger) return true
    if (mappingHasEmptyTriggerValue(dependency, trigger.data_source)) {
      return true
    }
  }
  return false
}

export function getMappedOptionsForTriggerValue(
  dependency: CheckpointDependency | null | undefined,
  triggerValue: string | null | undefined,
  dataSource: any[] = []
): any[] {
  if (!dependency?.reference || !triggerValue) return []
  const allowed = getMappedValues(dependency.mappings, triggerValue)
  return (dataSource || []).filter((opt) =>
    includesIgnoreCase(allowed, opt.value)
  )
}

export function isAnswerValidForMapping(
  answer: any,
  dependency: CheckpointDependency | null | undefined,
  triggerValue: string | null | undefined
): boolean {
  if (!dependency?.reference) return true
  if (!triggerValue) return !answer?.value && !answer?.label
  const allowed = getMappedValues(dependency.mappings, triggerValue)
  if (!answer?.value && !answer?.label) return true
  return includesIgnoreCase(allowed, answer.value)
}

export function findTemplateCheckpointByReference(
  templateSections: any[] = [],
  reference: string | null | undefined
): any | null {
  const parsed = parseCheckpointReference(reference)
  if (!parsed) return null
  const section = (templateSections || []).find(
    (s) => s.name === parsed.sectionName
  )
  const sub = (section?.sub_sections || []).find(
    (s) => s.name === parsed.subSectionName
  )
  return (
    (sub?.checkpoints || []).find((c) => c.name === parsed.checkpointName) ||
    null
  )
}

export function findReportCheckpointByReference(
  reportSections: any[] = [],
  reference: string | null | undefined
): any | null {
  const parsed = parseCheckpointReference(reference)
  if (!parsed) return null
  for (const section of reportSections || []) {
    if (section?.name !== parsed.sectionName) continue
    for (const sub of section.subSections || section.sub_sections || []) {
      if (sub?.name !== parsed.subSectionName) continue
      const cp = (sub.checkpoints || []).find(
        (c) => c.name === parsed.checkpointName
      )
      if (cp) return cp
    }
  }
  return null
}

export function getTriggerAnswerValue(triggerCheckpoint: any): string | null {
  const value = triggerCheckpoint?.value
  if (!value) return null
  if (typeof value === 'object') return value.value || null
  return value
}

export function listDependentsOfTrigger(
  templateSections: any[] = [],
  reference: string
): any[] {
  if (!reference) return []
  const dependents: any[] = []
  for (const section of templateSections || []) {
    for (const sub of section.sub_sections || []) {
      for (const cp of sub.checkpoints || []) {
        const dep = getDependency(cp)
        if (dep?.reference === reference) {
          dependents.push(cp)
        }
      }
    }
  }
  return dependents
}

export function isUsedAsTrigger(
  sections: any[] = [],
  reference: string | null | undefined
): boolean {
  return listDependentsOfTrigger(sections, reference || '').length > 0
}

export function isCheckpointUsedAsTrigger(
  sections: any[] = [],
  checkpoint: any
): boolean {
  const reference = flattenCheckpoints(sections).find(
    (cp) => cp.checkpoint === checkpoint
  )?.reference
  return isUsedAsTrigger(sections, reference)
}

export function clearDependentsReferencing(
  sections: any[],
  reference: string | null | undefined
) {
  if (!reference) return
  flattenCheckpoints(sections).forEach(({ checkpoint }) => {
    if (getDependency(checkpoint)?.reference === reference) {
      clearDependency(checkpoint)
    }
  })
}

/** Rewrite dependency.reference when a path segment is renamed. */
export function rewriteDependencyReferences(
  sections: any[],
  previousReference: string,
  nextReference: string
) {
  if (!previousReference || previousReference === nextReference) return
  flattenCheckpoints(sections).forEach(({ checkpoint }) => {
    const dep = checkpoint?.options?.dependency
    if (!dep) return
    if (dep.reference === previousReference) {
      dep.reference = nextReference
    }
    if (dep.trigger_name === previousReference) {
      dep.reference = nextReference
      delete dep.trigger_name
    }
  })
}

/** Rewrite references that start with a path prefix (section or section.sub). */
export function rewriteDependencyReferencePrefix(
  sections: any[],
  previousPrefix: string,
  nextPrefix: string
) {
  if (!previousPrefix || previousPrefix === nextPrefix) return
  // Walk raw sections so we catch deps even when flatten skips incomplete names mid-edit
  for (const section of sections || []) {
    for (const sub of section.sub_sections || []) {
      for (const cp of sub.checkpoints || []) {
        const dep = cp?.options?.dependency
        if (!dep?.reference) continue
        if (
          dep.reference === previousPrefix ||
          dep.reference.startsWith(`${previousPrefix}.`)
        ) {
          dep.reference =
            nextPrefix + dep.reference.slice(previousPrefix.length)
        }
      }
    }
  }
}
